import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Calendar } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from './ui/button';
import { SEO } from './SEO';
import { buildBreadcrumb, buildServiceSchema, buildFAQSchema } from '../lib/seo-schemas';

const serviceDetails = {
  'college-admissions': {
    icon: 'GraduationCap',
    title: 'College Admissions Support',
    subtitle: 'Navigate the admissions process with expert guidance',
    description: 'Getting into the right career college program is the first step toward your professional success. We simplify the complex admissions process and ensure your application stands out.',
    benefits: [
      'Personalized college matching based on your career goals',
      'Application review and optimization',
      'Document preparation and verification',
      'Admission requirement guidance',
      'Application timeline management',
      'Follow-up and status tracking'
    ],
    process: [
      {
        step: 1,
        title: 'Initial Consultation',
        description: 'We assess your academic background, career goals, and preferences to identify the best college programs.'
      },
      {
        step: 2,
        title: 'Program Selection',
        description: 'Match you with suitable programs at Granville, Cambria, VCCT, or ACC based on your profile.'
      },
      {
        step: 3,
        title: 'Application Preparation',
        description: 'Help you gather all required documents, complete application forms, and craft compelling personal statements.'
      },
      {
        step: 4,
        title: 'Submission & Follow-up',
        description: 'Submit your application and actively follow up with colleges to ensure timely processing.'
      }
    ],
    colleges: ['Cambria College', 'Granville College', 'Swenam College', 'VCCT', 'Avalon Community College', 'Eton College', 'Multihexa College', 'Taylor Pro College'],
    faqs: [
      {
        q: 'What are the typical admission requirements?',
        a: 'Requirements vary by program but generally include Grade 12 completion or equivalent, English proficiency proof (for international students), and sometimes program-specific prerequisites.'
      },
      {
        q: 'How long does the admission process take?',
        a: 'Typically 2-4 weeks from application submission to decision, though this can vary by college and program.'
      },
      {
        q: 'Can I apply to multiple programs?',
        a: 'Yes! We recommend applying to 2-3 programs to maximize your chances of admission.'
      }
    ]
  },
  'career-path-planning': {
    icon: 'MapPin',
    title: 'Career Path Planning',
    subtitle: 'Discover your ideal program and career trajectory',
    description: 'Not sure which program is right for you? Our expert career counselors help you identify programs that align with your interests, skills, and long-term career goals.',
    benefits: [
      'One-on-one career assessment sessions',
      'Industry trend analysis and job market insights',
      'Program comparison across colleges',
      'Long-term career roadmap development',
      'Skills gap analysis',
      'Salary expectations and growth potential'
    ],
    process: [
      {
        step: 1,
        title: 'Career Assessment',
        description: 'Complete detailed assessments to understand your strengths, interests, and career aspirations.'
      },
      {
        step: 2,
        title: 'Market Research',
        description: 'We research current job market trends and future opportunities in your fields of interest.'
      },
      {
        step: 3,
        title: 'Program Matching',
        description: 'Identify programs that provide the best pathway to your desired career.'
      },
      {
        step: 4,
        title: 'Action Plan',
        description: 'Develop a comprehensive roadmap from education to career launch.'
      }
    ],
    colleges: ['Cambria College', 'Granville College', 'Swenam College', 'VCCT', 'Avalon Community College', 'Eton College', 'Multihexa College', 'Taylor Pro College'],
    faqs: [
      {
        q: 'How do you help me choose the right career path?',
        a: 'We use comprehensive assessments, market research, and one-on-one counseling to understand your strengths and match them with in-demand careers.'
      },
      {
        q: 'What if I\'m unsure about my interests?',
        a: 'That\'s completely normal! Our counselors specialize in helping undecided students explore options and discover their passions.'
      }
    ]
  },
  'domestic-student-support': {
    icon: 'Home',
    title: 'Domestic Student Support',
    subtitle: 'Dedicated support for Canadian residents pursuing career college programs',
    description: 'Whether you\'re a high-school graduate, an adult learner returning to school, or a career-changer, Elevated Education guides Canadian residents and permanent residents through every step — from identifying provincial student aid to settling into your program.',
    benefits: [
      'Provincial student aid guidance (BC StudentAid, OSAP, etc.)',
      'Domestic tuition and bursary planning',
      'High-school-to-college transition support',
      'Adult learner and career-changer coaching',
      'Local accommodation referrals',
      'Ongoing 1-on-1 mentorship throughout your program'
    ],
    process: [
      {
        step: 1,
        title: 'Free Consultation',
        description: 'We discuss your residency, academic background, and career goals to understand what kind of support fits best.'
      },
      {
        step: 2,
        title: 'Funding Plan',
        description: 'Identify the provincial loans, grants, and bursaries you qualify for as a Canadian resident — and how to apply for each.'
      },
      {
        step: 3,
        title: 'Application Support',
        description: 'Guide you through every form, deadline, and document at our partner BC career colleges.'
      },
      {
        step: 4,
        title: 'Enrolment & Beyond',
        description: 'Help you transition smoothly from acceptance letter to first day — and stay available for support throughout your program.'
      }
    ],
    colleges: ['Cambria College', 'Granville College', 'Swenam College', 'VCCT', 'Avalon Community College', 'Eton College', 'Multihexa College', 'Taylor Pro College'],
    faqs: [
      {
        q: 'Who counts as a domestic student in BC?',
        a: 'Canadian citizens, permanent residents, and protected persons typically qualify for domestic tuition rates and provincial student aid programs like BC StudentAid.'
      },
      {
        q: 'What is the difference between a student loan and a grant?',
        a: 'A student loan (e.g. BC StudentAid) must be repaid after graduation. Grants and bursaries are free money awarded based on financial need or merit. We help you identify every option you qualify for.'
      },
      {
        q: 'Can I work while studying as a domestic student?',
        a: 'Yes — there are no work-hour restrictions for Canadian residents. Many of our students balance part-time work with their studies, especially in programs with flexible scheduling.'
      }
    ]
  },
  'application-assistance': {
    icon: 'FileText',
    title: 'Application Assistance',
    subtitle: 'Professional help with documents and applications',
    description: 'Your application is your first impression. We ensure every element of your application is polished, complete, and positioned to maximize your chances of acceptance.',
    benefits: [
      'Application form completion support',
      'Personal statement writing and editing',
      'Resume/CV optimization',
      'Reference letter coordination',
      'Document formatting and organization',
      'Quality assurance review'
    ],
    process: [
      {
        step: 1,
        title: 'Document Collection',
        description: 'Gather all required documents including transcripts, certificates, and identification.'
      },
      {
        step: 2,
        title: 'Application Drafting',
        description: 'Help you complete application forms accurately and write compelling personal statements.'
      },
      {
        step: 3,
        title: 'Review & Polish',
        description: 'Multiple rounds of review to ensure error-free, professional applications.'
      },
      {
        step: 4,
        title: 'Submission',
        description: 'Submit your complete application package and track its progress.'
      }
    ],
    colleges: ['Cambria College', 'Granville College', 'Swenam College', 'VCCT', 'Avalon Community College', 'Eton College', 'Multihexa College', 'Taylor Pro College'],
    faqs: [
      {
        q: 'Will you write my personal statement for me?',
        a: 'We help you craft your personal statement by providing guidance, structure, and editing, but the content comes from your own experiences and goals.'
      },
      {
        q: 'What makes a strong application?',
        a: 'A strong application has clear career goals, demonstrates genuine interest in the program, highlights relevant experience, and is error-free and well-organized.'
      }
    ]
  },
  'one-on-one-mentorship': {
    icon: 'Users',
    title: 'One-on-One Mentorship',
    subtitle: 'Personalized guidance throughout your journey',
    description: 'Get dedicated support from experienced education advisors who understand the Canadian career college system and are committed to your success.',
    benefits: [
      'Dedicated advisor assigned to you',
      'Regular check-ins and progress updates',
      'Personalized guidance based on your needs',
      'Academic performance monitoring',
      'Career development support',
      'Ongoing support even after admission'
    ],
    process: [
      {
        step: 1,
        title: 'Mentor Assignment',
        description: 'You\'re paired with an experienced advisor who specializes in your field of interest.'
      },
      {
        step: 2,
        title: 'Goal Setting',
        description: 'Work together to set clear, achievable educational and career goals.'
      },
      {
        step: 3,
        title: 'Regular Sessions',
        description: 'Scheduled sessions to review progress, address challenges, and adjust strategies.'
      },
      {
        step: 4,
        title: 'Continuous Support',
        description: 'Ongoing support via email, phone, and video calls throughout your journey.'
      }
    ],
    colleges: ['Cambria College', 'Granville College', 'Swenam College', 'VCCT', 'Avalon Community College', 'Eton College', 'Multihexa College', 'Taylor Pro College'],
    faqs: [
      {
        q: 'How often will I meet with my mentor?',
        a: 'Typically bi-weekly or monthly, depending on your needs and stage in the process. You can also reach out anytime via email.'
      },
      {
        q: 'What qualifications do mentors have?',
        a: 'Our mentors are experienced education advisors with deep knowledge of Canadian career colleges and proven track records of student success.'
      }
    ]
  },
  'scholarship-guidance': {
    icon: 'Award',
    title: 'Scholarship & Financial Aid Guidance',
    subtitle: 'Find funding opportunities for your education',
    description: 'Education is an investment in your future. We help you identify and apply for scholarships, bursaries, and financial aid to make your education more affordable.',
    benefits: [
      'Scholarship database access',
      'Personalized scholarship matching',
      'Application strategy development',
      'Essay and application support',
      'Financial planning assistance',
      'Government funding guidance'
    ],
    process: [
      {
        step: 1,
        title: 'Financial Assessment',
        description: 'Evaluate your financial situation and funding needs.'
      },
      {
        step: 2,
        title: 'Opportunity Research',
        description: 'Identify relevant scholarships, bursaries, and financial aid programs.'
      },
      {
        step: 3,
        title: 'Application Support',
        description: 'Help you prepare strong scholarship applications with compelling essays.'
      },
      {
        step: 4,
        title: 'Follow-up',
        description: 'Track applications and explore additional funding sources as needed.'
      }
    ],
    colleges: ['Cambria College', 'Granville College', 'Swenam College', 'VCCT', 'Avalon Community College', 'Eton College', 'Multihexa College', 'Taylor Pro College'],
    faqs: [
      {
        q: 'What kinds of scholarships and bursaries can I apply for?',
        a: 'We help you identify provincial bursaries, college-specific awards, and merit/need-based scholarships available to Canadian residents at our partner BC career colleges.'
      },
      {
        q: 'Can I get financial aid for career college programs?',
        a: 'Yes — many programs are eligible for BC StudentAid loans, grants, and bursaries. We guide you through every funding option you qualify for as a domestic student.'
      }
    ]
  }
};

export const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const service = serviceDetails[serviceId];

  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  if (!service) {
    return (
      <div className="min-h-screen pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900">Service Not Found</h1>
          <Link to="/" className="text-blue-600 hover:text-blue-700 mt-4 inline-block">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  const Icon = LucideIcons[service.icon];

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const contactElement = document.getElementById('contact');
      if (contactElement) {
        contactElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 500);
  };

  const seoSchemas = [
    buildServiceSchema({
      name: service.title,
      description: service.description,
      slug: serviceId,
    }),
    buildBreadcrumb([
      { name: 'Home', path: '/' },
      { name: 'Services', path: '/#services' },
      { name: service.title, path: `/services/${serviceId}` },
    ]),
    buildFAQSchema(service.faqs),
  ];

  return (
    <div className="min-h-screen pt-32 pb-16 bg-gray-50">
      <SEO
        title={service.title}
        description={service.description}
        path={`/services/${serviceId}`}
        schema={seoSchemas}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link 
          to="/"
          className="inline-flex items-center text-gray-600 hover:text-blue-600 mb-8 transition-colors duration-200"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="bg-white rounded-2xl p-12 shadow-lg mb-12">
          <div className="flex items-start space-x-6">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl flex items-center justify-center text-white flex-shrink-0">
              {Icon && <Icon className="w-10 h-10" />}
            </div>
            <div className="flex-1">
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                {service.title}
              </h1>
              <p className="text-xl text-gray-600 mb-6">
                {service.subtitle}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                {service.description}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Benefits */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                What You Get
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {service.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Process */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                How It Works
              </h2>
              <div className="space-y-6">
                {service.process.map((step, index) => (
                  <div key={index} className="flex space-x-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {step.step}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {step.title}
                      </h3>
                      <p className="text-gray-600">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQs */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Frequently Asked Questions
              </h2>
              <div className="space-y-6">
                {service.faqs.map((faq, index) => (
                  <div key={index}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {faq.q}
                    </h3>
                    <p className="text-gray-600">
                      {faq.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* CTA Card */}
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 shadow-lg text-white sticky top-24">
              <h3 className="text-2xl font-bold mb-4">
                Ready to Get Started?
              </h3>
              <p className="mb-6 opacity-90">
                Book a free consultation to discuss how we can help you achieve your educational goals.
              </p>
              <Button 
                onClick={scrollToContact}
                className="w-full bg-white text-blue-600 hover:bg-gray-100"
                size="lg"
              >
                <Calendar className="mr-2 w-5 h-5" />
                Book Free Consultation
              </Button>
            </div>

            {/* Partner Colleges */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Partner Colleges
              </h3>
              <div className="space-y-3">
                {service.colleges.map((college, index) => (
                  <div key={index} className="flex items-center space-x-2 text-gray-700">
                    <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span>{college}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Other Services */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Other Services
              </h3>
              <div className="space-y-2">
                {Object.keys(serviceDetails)
                  .filter(key => key !== serviceId)
                  .slice(0, 3)
                  .map((key) => (
                    <Link
                      key={key}
                      to={`/services/${key}`}
                      className="block text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      {serviceDetails[key].title}
                    </Link>
                  ))}
              </div>
              <Link
                to="/#services"
                className="block text-gray-600 hover:text-gray-900 mt-4 text-sm"
              >
                View All Services →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
